import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../utils/api';

// ── Action → human-readable label ────────────────────────────────────────────
const ACTION_LABELS = {
  'auth.register':              'Registered',
  'auth.login':                 'Logged In',
  'auth.profile_updated':       'Profile Updated',
  'child.created':              'Child Created',
  'child.updated':              'Child Updated',
  'child.deleted':              'Child Deleted',
  'story.generated':            'Story Generated',
  'story.deleted':              'Story Deleted',
  'story.favorited':            'Story Favorited',
  'story.unfavorited':          'Story Unfavorited',
  'story.pdf_exported':         'PDF Exported',
  'story.quiz_generated':       'Quiz Generated',
  'story.published':            'Story Published',
  'story.unpublished':          'Story Unpublished',
  'illustration.regenerated':   'Illustration Regenerated',
  'narration.ai_generated':     'Smart Narration Generated',
  'voice.profile_created':      'Voice Profile Created',
  'voice.profile_deleted':      'Voice Profile Deleted',
  'share.link_created':         'Share Link Created',
  'share.link_deleted':         'Share Link Deleted',
  'topup.purchased':            'Top-Up Purchased',
  'referral.link_created':      'Referral Link Created',
  'referral.claimed':           'Referral Story Claimed',
  'subscription.upgraded':      'Plan Upgraded',
  'subscription.cancelled':     'Subscription Cancelled',
  'subscription.cancel_scheduled': 'Cancellation Scheduled',
  'admin.user_updated':         'User Updated',
  'admin.user_suspended':       'User Suspended',
  'admin.user_unsuspended':     'User Unsuspended',
  'admin.plan_changed':         'Plan Changed (Admin)',
  'admin.admin_toggled':        'Admin Role Toggled',
  'admin.credits_adjusted':     'Credits Adjusted',
  'admin.user_deleted':         'User Deleted',
  'admin.story_deleted':        'Story Deleted (Admin)',
  'admin.story_hidden':         'Story Hidden',
  'admin.story_unhidden':       'Story Unhidden',
  'admin.story_featured':       'Story Featured',
  'admin.story_unfeatured':     'Story Unfeatured',
  'admin.broadcast_sent':       'Broadcast Email Sent',
};

// ── Category badge config ─────────────────────────────────────────────────────
const CATEGORY_CONFIG = {
  auth:         { color: '#2563eb', bg: '#eff6ff', icon: 'bi-shield-lock'    },
  story:        { color: '#7c3aed', bg: '#f5f3ff', icon: 'bi-book'           },
  child:        { color: '#059669', bg: '#f0fdf4', icon: 'bi-person-hearts'  },
  narration:    { color: '#d97706', bg: '#fffbeb', icon: 'bi-headphones'     },
  voice:        { color: '#db2777', bg: '#fdf2f8', icon: 'bi-mic'            },
  community:    { color: '#0891b2', bg: '#f0f9ff', icon: 'bi-globe2'         },
  subscription: { color: '#16a34a', bg: '#f0fdf4', icon: 'bi-credit-card'   },
  admin:        { color: '#dc2626', bg: '#fef2f2', icon: 'bi-shield-exclamation' },
  referral:     { color: '#d97706', bg: '#fffbeb', icon: 'bi-gift'              },
};

const catCfg = (cat) => CATEGORY_CONFIG[cat] || { color: 'var(--text-muted2)', bg: 'var(--header-bg)', icon: 'bi-activity' };

const CategoryBadge = ({ category }) => {
  const cfg = catCfg(category);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 600,
      color: cfg.color, background: cfg.bg,
    }}>
      <i className={`bi ${cfg.icon}`} style={{ fontSize: '10px' }}></i>
      {category}
    </span>
  );
};

const MetadataView = ({ metadata }) => {
  if (!metadata || Object.keys(metadata).length === 0) return <span style={{ color: 'var(--text-soft)', fontSize: '12px' }}>—</span>;
  return (
    <details>
      <summary style={{ cursor: 'pointer', color: '#6f42c1', fontSize: '12px', userSelect: 'none' }}>
        {Object.keys(metadata).length} field{Object.keys(metadata).length !== 1 ? 's' : ''}
      </summary>
      <pre style={{
        fontSize: '11px', background: '#f8f7ff', border: '1px solid #ede8f8',
        borderRadius: '6px', padding: '8px', marginTop: '4px', marginBottom: 0,
        maxWidth: '320px', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
      }}>
        {JSON.stringify(metadata, null, 2)}
      </pre>
    </details>
  );
};

const CATEGORIES = ['auth','story','child','narration','voice','community','subscription','admin'];

export default function AdminLogs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs,       setLogs]     = useState([]);
  const [total,      setTotal]    = useState(0);
  const [pages,      setPages]    = useState(1);
  const [loading,    setLoading]  = useState(true);
  const [error,      setError]    = useState('');
  const [categories, setCategories] = useState([]);
  const [actions,    setActions]  = useState([]);

  const category = searchParams.get('category') || '';
  const action   = searchParams.get('action')   || '';
  const search   = searchParams.get('search')   || '';
  const from     = searchParams.get('from')     || '';
  const to       = searchParams.get('to')       || '';
  const page     = parseInt(searchParams.get('page') || '1');

  const [searchInput, setSearchInput] = useState(search);

  const set = (key, val) => {
    const p = new URLSearchParams(searchParams);
    if (val) p.set(key, val); else p.delete(key);
    p.delete('page');
    setSearchParams(p);
  };

  const fetchLogs = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 50 };
    if (category) params.category = category;
    if (action)   params.action   = action;
    if (search)   params.search   = search;
    if (from)     params.from     = from;
    if (to)       params.to       = to;

    API.get('/admin/logs', { params })
      .then(({ data }) => {
        setLogs(data.logs || []);
        setTotal(data.total || 0);
        setPages(data.pages || 1);
        setCategories(data.categories || CATEGORIES);
        setActions(data.actions || []);
      })
      .catch(e => setError(e.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  }, [page, category, action, search, from, to]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const goPage = (p) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', p);
    setSearchParams(params);
  };

  return (
    <AdminLayout title="Activity Logs">
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
        <div>
          <h4 className="fw-bold mb-0" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive" }}>
            Activity Logs
          </h4>
          <div style={{ color: 'var(--text-muted2)', fontSize: '13px' }}>
            {total.toLocaleString()} event{total !== 1 ? 's' : ''} · last 180 days retained
          </div>
        </div>
        <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={fetchLogs}>
          <i className="bi bi-arrow-clockwise me-1"></i>Refresh
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-body p-3">
          <div className="row g-2 align-items-end">
            {/* Search */}
            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold" style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>Search</label>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white border-end-0">
                  <i className="bi bi-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="User, email, or action…"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && set('search', searchInput)}
                />
              </div>
            </div>

            {/* Category */}
            <div className="col-6 col-md-2">
              <label className="form-label fw-semibold" style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>Category</label>
              <select className="form-select form-select-sm" value={category} onChange={e => set('category', e.target.value)}>
                <option value="">All categories</option>
                {categories.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* Action */}
            <div className="col-6 col-md-3">
              <label className="form-label fw-semibold" style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>Action</label>
              <select className="form-select form-select-sm" value={action} onChange={e => set('action', e.target.value)}>
                <option value="">All actions</option>
                {actions.map(a => (
                  <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>
                ))}
              </select>
            </div>

            {/* Date range */}
            <div className="col-6 col-md-2">
              <label className="form-label fw-semibold" style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>From</label>
              <input type="date" className="form-control form-control-sm" value={from} onChange={e => set('from', e.target.value)} />
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label fw-semibold" style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>To</label>
              <input type="date" className="form-control form-control-sm" value={to} onChange={e => set('to', e.target.value)} />
            </div>

            {/* Buttons */}
            <div className="col-12 col-md-auto d-flex gap-2 mt-1">
              <button className="btn btn-sm btn-primary rounded-pill px-3" onClick={() => set('search', searchInput)}>
                <i className="bi bi-funnel me-1"></i>Filter
              </button>
              <button
                className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                onClick={() => {
                  setSearchInput('');
                  setSearchParams({});
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Quick category pills ── */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        <button
          className={`btn btn-sm rounded-pill px-3 ${!category ? 'btn-primary' : 'btn-outline-secondary'}`}
          style={{ fontSize: '12px' }}
          onClick={() => set('category', '')}
        >
          All
        </button>
        {CATEGORIES.map(c => {
          const cfg = catCfg(c);
          const active = category === c;
          return (
            <button
              key={c}
              className="btn btn-sm rounded-pill px-3"
              style={{
                fontSize: '12px',
                background: active ? cfg.color : cfg.bg,
                color: active ? '#fff' : cfg.color,
                border: `1px solid ${cfg.color}33`,
              }}
              onClick={() => set('category', c)}
            >
              <i className={`bi ${cfg.icon} me-1`}></i>{c}
            </button>
          );
        })}
      </div>

      {/* ── Table ── */}
      {error && <div className="alert alert-danger rounded-3">{error}</div>}

      <div className="card border-0 shadow-sm rounded-4">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" />
            <div className="mt-2 text-muted">Loading logs…</div>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-journal-x fs-1 text-muted d-block mb-2"></i>
            <p className="text-muted mb-0">No logs found for the selected filters.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0" style={{ fontSize: '13px' }}>
              <thead style={{ background: '#f8f7ff' }}>
                <tr>
                  <th className="ps-3 py-3 fw-semibold" style={{ color: 'var(--text-muted2)', width: '160px' }}>Time</th>
                  <th className="py-3 fw-semibold" style={{ color: 'var(--text-muted2)', width: '90px' }}>Category</th>
                  <th className="py-3 fw-semibold" style={{ color: 'var(--text-muted2)' }}>Action</th>
                  <th className="py-3 fw-semibold" style={{ color: 'var(--text-muted2)' }}>User</th>
                  <th className="py-3 fw-semibold" style={{ color: 'var(--text-muted2)' }}>Details</th>
                  <th className="pe-3 py-3 fw-semibold" style={{ color: 'var(--text-muted2)', width: '90px' }}>IP</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log._id}>
                    <td className="ps-3 py-2" style={{ color: 'var(--text-muted2)', fontSize: '12px', whiteSpace: 'nowrap' }}>
                      {new Date(log.createdAt).toLocaleString('en-US', {
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                    <td className="py-2">
                      <CategoryBadge category={log.category} />
                    </td>
                    <td className="py-2">
                      <div className="fw-semibold" style={{ color: log.isAdmin ? '#dc2626' : '#2d1b69' }}>
                        {ACTION_LABELS[log.action] || log.action}
                        {log.isAdmin && (
                          <span className="badge ms-1" style={{ background: '#fef2f2', color: '#dc2626', fontSize: '10px', fontWeight: 600, border: '1px solid #fecaca' }}>
                            admin
                          </span>
                        )}
                      </div>
                      <div style={{ color: 'var(--text-soft)', fontSize: '11px', fontFamily: 'monospace' }}>{log.action}</div>
                    </td>
                    <td className="py-2">
                      <div className="fw-medium" style={{ color: 'var(--text-body)' }}>{log.userName || '—'}</div>
                      <div style={{ color: 'var(--text-soft)', fontSize: '11px' }}>{log.userEmail || '—'}</div>
                    </td>
                    <td className="py-2">
                      <MetadataView metadata={log.metadata} />
                    </td>
                    <td className="pe-3 py-2" style={{ color: 'var(--text-soft)', fontSize: '11px', fontFamily: 'monospace' }}>
                      {log.ip || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="d-flex align-items-center justify-content-between px-3 py-3 border-top" style={{ background: 'var(--header-bg)' }}>
            <span style={{ fontSize: '13px', color: 'var(--text-muted2)' }}>
              Page {page} of {pages} · {total.toLocaleString()} total
            </span>
            <div className="d-flex gap-1">
              <button
                className="btn btn-sm btn-outline-secondary rounded-pill"
                disabled={page <= 1}
                onClick={() => goPage(page - 1)}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
              {Array.from({ length: Math.min(5, pages) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 2, pages - 4));
                const p = start + i;
                if (p > pages) return null;
                return (
                  <button
                    key={p}
                    className={`btn btn-sm rounded-pill ${p === page ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => goPage(p)}
                    style={{ minWidth: '34px' }}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                className="btn btn-sm btn-outline-secondary rounded-pill"
                disabled={page >= pages}
                onClick={() => goPage(page + 1)}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
